import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";

import {
  updateEntityFlowResourceMutation,
  deleteEntityFlowResourceMutation,
} from "UnitFlowV3/modules/EntityFlow/Mutation";
import { isFolder } from "Utils/EntityFlowUtils";
import { getEntireUnitFlowFeedQuery } from "UnitFlowV3/modules/EntityFlow/Query";
import {
  getUnitCombinedLibraryQueryFromCache,
  writeUnitCombinedLibraryQueryInCache,
} from "./GraphqlHelper";

import {
  getEntireUnitFlowFeedQueryFromCache,
  writeUnitFlowItemInCache,
  getUnitFlowItemFromCache,
} from "UnitFlowV3/modules/EntityFlow/GraphqlHelper";
import {
  getToastMsgForResourceUpdate,
  getUnitFlowFeedVariablesMemoized,
} from "UnitFlowV3/modules/EntityFlow/utils";
import { getUnitCombinedLibraryQuery } from "./Query";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getCombinedLibraryFeedVariablesMemoized } from "./utils";
import { deleteAssessmentMutation } from "Assessments/modules/AssessmentMutations";
import {
  removeUnitFlowResourcesFromFeed,
  updateAncestorChildCountRecursively,
  recalculateMaxDepth,
  rollbackResourcesInCache,
} from "UnitFlowV3/modules/EntityFlow/Module";
import { getUpdatedTaggedPractices } from "SnP_v2/modules/SnPUtils";

export const updateCombinedLibraryResources = ({ id, label, attachment }) => {
  return async (dispatch, getState) => {
    const state = getState();

    // variables for updating right pane resources
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");

    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const feedQueryVariables = getCombinedLibraryFeedVariablesMemoized({
      unitPlanId,
      filters: combinedLibraryFilters,
    });

    const variables = {
      input: {
        id,
        label,
        attachment,
      },
    };

    // variables for updating unit flow feed
    const filters = _.get(state, "unitFlow.filters", {});

    const userInfo = getUserInfo({ portalType: "PLANNER" });

    const unitFlowfeedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    const rollbackResources = [];
    try {
      const unitFlowFeedData = getEntireUnitFlowFeedQueryFromCache(
        unitFlowfeedQueryVariables
      );

      const unitFlowFeed = _.cloneDeep(
        _.get(unitFlowFeedData, "node.unitFlowFeed.unitFlowFlatFeed.edges", [])
      );
      _.forEach(unitFlowFeed, edge => {
        const { node } = edge;
        if (node.item?.id !== id) return;

        const cachedUnitFlowNode = getUnitFlowItemFromCache({
          id: node.id,
          variables: unitFlowfeedQueryVariables,
        });

        if (cachedUnitFlowNode) {
          // used to rollback on error
          rollbackResources.push(cachedUnitFlowNode);

          const unitFlowNode = _.cloneDeep(cachedUnitFlowNode);

          unitFlowNode.label = label;

          if (unitFlowNode?.item?.attachment) {
            const nodeAttachment = unitFlowNode.item.attachment;
            nodeAttachment.name = label;
            if (nodeAttachment.attachmentType === "LINK") {
              nodeAttachment.url = attachment.url;
              nodeAttachment.thumbUrl = _.get(attachment, "thumbUrl", "");
            } else if (nodeAttachment.attachmentType === "NOTE") {
              nodeAttachment.name = label;
              nodeAttachment.attachmentTitle = label;
              nodeAttachment.title = label;
            } else {
              nodeAttachment.name = label;
            }
          }

          writeUnitFlowItemInCache({
            id: node.id,
            variables: unitFlowfeedQueryVariables,
            data: unitFlowNode,
          });
        }
      });

      await client.mutate({
        mutation: updateEntityFlowResourceMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            updateEntityFlowResource: {
              id,
              __typename: "EntityFlowResource",
            },
            __typename: "PlannerMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getUnitCombinedLibraryQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResources = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", {})
              );
              const itemIdx = _.findIndex(
                unitResources,
                ({ node }) => node.item.id === id
              );
              if (itemIdx === -1) return;

              const newItem = _.cloneDeep(unitResources[itemIdx]);

              const newItemNode = _.get(newItem, "node");
              newItemNode.label = label;
              if (newItemNode.item?.attachment) {
                const nodeAttachment = newItemNode.item.attachment;

                nodeAttachment.name = label;
                if (nodeAttachment.attachmentType === "LINK") {
                  nodeAttachment.url = attachment.url;
                  nodeAttachment.thumbUrl = _.get(attachment, "thumbUrl", "");
                } else if (nodeAttachment.attachmentType === "NOTE") {
                  nodeAttachment.name = label;
                  nodeAttachment.attachmentTitle = label;
                  nodeAttachment.title = label;
                } else {
                  nodeAttachment.name = label;
                }
              }
              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: {
                      edges: {
                        [itemIdx]: { $set: newItem },
                      },
                    },
                  },
                },
              });
            }
          );

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: unitFlowfeedQueryVariables,
            },
            data => {
              if (!data) return;

              const updatedEdges = _.map(unitFlowFeed, edge => {
                const { node } = edge;
                if (node.item?.id === id) node.label = label;
                return edge;
              });

              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });
      dispatch(setToastMsg(getToastMsgForResourceUpdate("FILE")));
    } catch (error) {
      rollbackResourcesInCache({ rollbackResources });

      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const deleteCombinedLibraryResources = ({ ids }) => {
  return async (dispatch, getState) => {
    const state = getState();
    // variables for updating right pane resources
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");

    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const feedQueryVariables = getCombinedLibraryFeedVariablesMemoized({
      unitPlanId,
      filters: combinedLibraryFilters,
    });

    // variables for updating unit flow feed
    const filters = _.get(state, "unitFlow.filters", {});

    const userInfo = getUserInfo({ portalType: "PLANNER" });

    const unitFlowfeedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });
    let countOfDeletedItems = 0;

    try {
      await client.mutate({
        mutation: deleteEntityFlowResourceMutation,
        variables: { input: { ids } },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            deleteEntityFlowResource: true,
            __typename: "PlannerMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getUnitCombinedLibraryQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResources = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", {})
              );

              const updatedEdges = _.filter(
                unitResources,
                ({ node }) => !_.includes(ids, node.item.id)
              );

              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: unitFlowfeedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitFlowFeed = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed")
              );

              const unitFlowFeedNodes = _.map(unitFlowFeed.edges, "node");
              const itemIdsToDelete = _.map(
                _.filter(unitFlowFeed?.edges, ({ node }) => {
                  const { item } = node;
                  return item && _.includes(ids, item?.id);
                }),
                ({ node }) => node.id
              );

              const items = _.filter(unitFlowFeedNodes, ({ id }) =>
                _.includes(itemIdsToDelete, id)
              );

              // update children and count of parents
              _.forEach(items, item => {
                const parent = _.find(
                  unitFlowFeedNodes,
                  ({ id }) => id === item.parent?.id
                );

                if (parent) {
                  parent.childrenV2 = _.filter(
                    parent.childrenV2,
                    ({ id }) => id !== item.id
                  );

                  updateAncestorChildCountRecursively({
                    id: parent.id,
                    items: unitFlowFeed.edges,
                    factor: isFolder(item) ? 0 : -1,
                  });

                  recalculateMaxDepth({
                    items: unitFlowFeed.edges,
                    id: parent.id,
                  });
                }
              });

              countOfDeletedItems = _.reduce(
                items,
                (result, item) => {
                  result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
                  result += isFolder(item) ? 0 : 1;
                  return result;
                },
                0
              );

              const updatedFeed = {
                ...unitFlowFeed,
                edges: _.filter(
                  unitFlowFeed.edges,
                  ({ node }) => !_.includes(itemIdsToDelete, node.id)
                ),
                resourceTypeBasedCount: {
                  ...unitFlowFeed?.resourceTypeBasedCount,
                  totalCount:
                    _.get(
                      unitFlowFeed,
                      "resourceTypeBasedCount.totalCount",
                      0
                    ) - countOfDeletedItems,
                },
              };

              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );
        },
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const deleteCombinedLibraryAssessment = ({ assessmentId }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");

    // Get combined library filters
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    const feedQueryVariables = getCombinedLibraryFeedVariablesMemoized({
      unitPlanId,
      filters: combinedLibraryFilters,
    });

    try {
      await client.mutate({
        mutation: deleteAssessmentMutation,
        variables: {
          id: assessmentId,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteAssessment: true,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteAssessment },
            },
          }
        ) => {
          if (deleteAssessment) {
            // Update the combined library cache
            cache.updateQuery(
              {
                query: getUnitCombinedLibraryQuery,
                variables: feedQueryVariables,
              },
              data => {
                if (!data) return;

                const unitResources = _.cloneDeep(
                  _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", {})
                );

                const updatedEdges = _.filter(
                  unitResources,
                  ({ node }) => node.item.id !== assessmentId
                );

                return update(data, {
                  node: {
                    unitFlowFeed: {
                      unitFlowFlatFeed: {
                        edges: { $set: updatedEdges },
                      },
                    },
                  },
                });
              }
            );

            // Remove the assessment from the unit flow feed
            dispatch(removeUnitFlowResourcesFromFeed({ ids: [assessmentId] }));
          }
        },
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const updateTaggedPracticesInCombinedLibrary = ({
  combinedLibraryFeedVariables,
  itemId,
  addedPractices,
  removedPractices,
  evaluationCycleId,
  userId,
}) => {
  const unitCombinedLibraryFeed = getUnitCombinedLibraryQueryFromCache(
    combinedLibraryFeedVariables
  );

  const edges = _.cloneDeep(
    _.get(
      unitCombinedLibraryFeed,
      "node.unitFlowFeed.unitFlowFlatFeed.edges",
      []
    )
  );

  const updatedEdges = _.map(edges, edge => {
    const node = _.get(edge, "node", {});

    if (
      _.get(node, "resourceType", "") !== "ASSESSMENT" ||
      _.get(node, "item.id", "") !== itemId
    )
      return edge;

    const taggedPractices = node?.item.taggedPractices ?? [];

    const updatedTaggedPractices = getUpdatedTaggedPractices({
      taggedPractices,
      evaluationCycleId,
      addedPractices,
      removedPractices,
      userId,
    });

    const updatedNode = {
      ...node,
      item: {
        ...node.item,

        taggedPractices: updatedTaggedPractices,
      },
    };

    const updatedEdge = { ...edge, node: updatedNode };

    return updatedEdge;
  });

  const updatedData = update(unitCombinedLibraryFeed, {
    node: {
      unitFlowFeed: {
        unitFlowFlatFeed: {
          edges: { $set: updatedEdges },
        },
      },
    },
  });

  writeUnitCombinedLibraryQueryInCache({
    variables: combinedLibraryFeedVariables,
    data: updatedData,
  });
};

export const updateLockedState = _.noop;
